// src/components/TopBar.jsx
import {
  ArrowLeftIcon,
  BellIcon,
  CaretDownIcon,
  GearIcon,
  InfoIcon,
  ListIcon,
  SignOutIcon,
  UserIcon,
} from '@phosphor-icons/react'; // Assuming you're using Phosphor Icons or similar
import { useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CustomerWithDriverOnboarding, Entity } from '../../types/apiTypes';
import { useScreenView } from '../../utils/ScreenViewContext';
import { useUser } from '../../utils/UserContext';
import { useBranding } from '../../providers/BrandingProvider';
import { Notification } from '../../types/dbTypes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useApi from '../../utils/AxiosWrapper';
import NotificationDropdown from './NotificationDropdown';
import { isPreActiveCustomer } from './customers/tabs/home/utils/demoData';

interface TopBarProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  showSideNav: boolean;
  setShowSideNav: (show: boolean) => void;
  currSubPage: string | null;
  setCurrSubPage: (page: string | null) => void;
}
const TopBar = ({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  showSideNav,
  setShowSideNav,
  currSubPage,
  setCurrSubPage,
}: TopBarProps) => {
  const navigate = useNavigate();
  const { userRecord, userId } = useUser();
  const { logoUrl } = useBranding();
  const [popupVisible, setPopupVisible] = useState(false);
  const location = useLocation();
  const { logout } = useUser();
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const popupRef = useRef<HTMLDivElement>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const queryClient = useQueryClient();
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );

  // Turn "cash-advances" -> "Cash Advances"
  const slugToTitle = (s: string) =>
    s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  // Heuristic for ids/dates you don't want as titles
  const looksLikeOpaqueId = (s: string) =>
    /^[0-9a-f-]{10,}$/.test(s) || /^\d{6,}$/.test(s);

  const getPathTitle = (pathname: string) => {
    const trimmed = pathname.replace(/\/+$/, '');
    const segments = trimmed.split('/').filter(Boolean);
    const last = segments[segments.length - 1] ?? '';
    const prev = segments[segments.length - 2] ?? '';

    const base = looksLikeOpaqueId(last) ? prev : last;
    return slugToTitle(base || 'Overview');
  };

  const resolveTitle = (pathname: string, fallback: string) => {
    const p = pathname.replace(/\/+$/, '');

    // --- Cash Advances rules ---
    if (/(^|\/)cash-advances(\/|$)/.test(p)) {
      const state = location.state as { loanType?: string } | null;
      return state?.loanType === 'fuel_advance'
        ? 'Fuel Advances'
        : 'Cash Advances';
    }
    if (p.endsWith('/cash-advance/consent')) return 'Sign Documents';
    if (/(^|\/)cash-advance(\/|$)/.test(p)) {
      const state = location.state as { loanType?: string } | null;
      return state?.loanType === 'fuel_advance'
        ? 'Fuel Advance Details'
        : 'Cash Advance Details';
    }

    // --- Assets (use segment checks) ---
    if (pathHasSegment(p, 'assets') && pathHasSegment(p, 'overview'))
      return 'Asset Overview';
    if (pathHasSegment(p, 'assets') && pathHasSegment(p, 'maintenance'))
      return 'Asset Maintenance';
    if (p.includes('/assets/odometer-history')) return 'Odometer History';
    if (p.includes('submit-mileage')) return 'Submit Mileage';

    if (p.includes('/settings')) return 'Settings';
    if (p.endsWith('/resources')) return 'My Resources';
    return fallback;
  };

  const pathHasSegment = (pathname: string, segment: string) => {
    const segments = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
    return segments.includes(segment);
  };

  const getBackConfig = (pathname: string) => {
    const p = pathname.replace(/\/+$/, '');

    const hasSingular = pathHasSegment(p, 'cash-advance');
    const hasPlural = pathHasSegment(p, 'cash-advances');

    // Banking: cash advance details page (plural "cash-advances" with "details" segment)
    if (hasPlural && pathHasSegment(p, 'details')) {
      const state = location.state as { loanType?: string } | null;
      const isFuel = state?.loanType === 'fuel_advance';
      return {
        title: isFuel ? 'Fuel Advance Details' : 'Cash Advance Details',
        to: '/dashboard/banking/cash-advances/active' as const,
      };
    }

    // Banking: individual cash advance flow (singular segment only)
    if (hasSingular && !hasPlural && !p.endsWith('/consent')) {
      const state = location.state as { advanceType?: string } | null;
      const isFuel = state?.advanceType === 'fuel_advance';
      return {
        title: isFuel ? 'Request a Fuel Advance' : 'Request a Cash Advance',
        to: '/dashboard/banking/cash-advances/active' as const,
      };
    }
    if (hasSingular && p.endsWith('/consent')) {
      return { title: 'Sign Documents', to: -1 as const };
    }

    // Assets (unchanged)
    if (pathHasSegment(p, 'create') && pathHasSegment(p, 'assets')) {
      return { title: 'Add Asset', to: '/dashboard/assets' as const };
    }
    if (p.includes('/assets/submit-mileage')) {
      return { title: 'Submit Mileage', to: '/dashboard/assets' as const };
    }
    if (p.includes('/assets/odometer-history')) {
      return {
        title: 'Odometer History',
        to: '/dashboard/assets/submit-mileage' as const,
      };
    }

    return null;
  };

  const togglePopup = () => {
    setPopupVisible(!popupVisible);
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        const toggleArea = event.target as HTMLElement;
        if (!toggleArea.closest('.user-box-toggle')) {
          setPopupVisible(false);
        }
      }
    };

    if (popupVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupVisible]);

  const { data: notifications, isLoading: loadingNotifications } = useQuery<
    Notification[] | null
  >({
    queryKey: ['notifications', userId],
    queryFn: () => fetchNotifications(),
    enabled:
      !!userId && userRecord?.userType === Entity.Customer && !isPreActive,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const fetchNotifications = async () => {
    const response = await callProtectedApi(
      `/notifications/customer/${userId ?? ''}`,
      'get',
      {}
    );
    // TanStack Query v5 throws if queryFn resolves to undefined (Sentry JAVASCRIPT-REACT-TC)
    return response?.data?.data?.notifications ?? null;
  };

  const refetchNotifications = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
  };

  const Title = ({ children }: { children: string }) => (
    <h1 className="text-[30px] font-bold text-primary">{children}</h1>
  );

  const BackTitle = ({
    to,
    children,
  }: {
    to: string | number;
    children: string;
  }) => {
    const navigate = useNavigate();
    return (
      <div className="flex flex-row items-center space-x-2 align-middle">
        <ArrowLeftIcon
          size={screenView === 'mobile' ? 20 : 24}
          className="text-primary hover:cursor-pointer"
          onClick={() =>
            typeof to === 'number' ? navigate(to) : navigate(to as string)
          }
        />
        {screenView === 'mobile' ? (
          <h1 className="text-[18px] font-[700] text-primary">{children}</h1>
        ) : (
          <Title>{children}</Title>
        )}
      </div>
    );
  };

  const renderTitleOrSearch = (currentTab: string): ReactNode => {
    const pathname = location.pathname || '';
    const back = getBackConfig(pathname);
    const baseTitle = getPathTitle(pathname);
    const title = resolveTitle(pathname, baseTitle);

    switch (currentTab) {
      case 'Home':
        return <Title>Home</Title>;

      case 'Banking':
        if (back) return <BackTitle to={back.to}>{back.title}</BackTitle>;
        return <Title>{title}</Title>;

      case 'Finances':
        return <Title>{title}</Title>;

      case 'Vendor Marketplace':
        return (
          <Title>
            {screenView === 'mobile' ? 'Vendors' : 'Vendor Marketplace'}
          </Title>
        );

      case 'Drivers':
        return <Title>{title}</Title>;

      case 'Assets':
        if (back) return <BackTitle to={back.to}>{back.title}</BackTitle>;
        return <Title>{title}</Title>;
      case 'Submit Mileage':
        return <Title>Submit Mileage</Title>;
      default:
        return <Title>{title}</Title>;
    }
  };

  const renderTitleOrSearchVendor = () => {
    const pathname = location.pathname || '';
    const back = getBackConfig(pathname);
    const title = resolveTitle(pathname, getPathTitle(pathname));
    return back ? (
      <BackTitle to={back.to}>{back.title}</BackTitle>
    ) : (
      <h1 className="text-[30px] font-bold text-primary">{title}</h1>
    );
  };

  const renderTitleOrSearchPartner = () => {
    const pathname = location.pathname || '';
    const back = getBackConfig(pathname);
    const title = resolveTitle(pathname, getPathTitle(pathname));
    return back ? (
      <BackTitle to={back.to}>{back.title}</BackTitle>
    ) : (
      <h1 className="text-[30px] font-bold text-primary">{title}</h1>
    );
  };

  const renderTitleOrSearchDriver = () => {
    const pathname = location.pathname || '';
    const back = getBackConfig(pathname);
    const title = resolveTitle(pathname, getPathTitle(pathname));
    return back ? (
      <BackTitle to={back.to}>{back.title}</BackTitle>
    ) : (
      <h1 className="text-[30px] font-bold text-primary">{title}</h1>
    );
  };

  const renderTitleOrSearchTablet = () => {
    const pathname = location.pathname || '';
    const back = getBackConfig(pathname);
    const baseTitle = getPathTitle(pathname);
    const title = resolveTitle(pathname, baseTitle);

    if (screenView === 'mobile' && title === 'Vendor Marketplace') {
      return <h1 className="text-[18px] font-[700] text-primary">Vendors</h1>;
    } else if (back && back?.title === 'Odometer History') {
      back.title = 'History';
    } else if (
      back &&
      (back?.title === 'Request a Cash Advance' ||
        back?.title === 'Request a Fuel Advance')
    ) {
      back.title = 'New Request';
    } else if (screenView === 'mobile' && title === 'Driver Performance') {
      return (
        <h1 className="text-[16px] font-[700] text-primary">
          Driver Performance
        </h1>
      );
    }

    if (back) {
      return <BackTitle to={back.to}>{back.title}</BackTitle>;
    }

    return <h1 className="text-[18px] font-[700] text-primary">{title}</h1>;
  };

  const renderUserBar = () => {
    switch (userRecord?.userType) {
      case Entity.Customer:
        return (
          <div className="flex w-full flex-row items-center justify-between bg-transparent align-middle">
            <div className="flex w-[35%] items-center pb-[16px]">
              {renderTitleOrSearch(currentTab)}
            </div>
            <div className="flex items-center space-x-4">
              <button
                className="relative rounded-full bg-gray-200 p-2 disabled:cursor-not-allowed"
                onClick={(e) => {
                  e.stopPropagation();
                  setNotificationsOpen(!notificationsOpen);
                }}
                disabled={isPreActive}
              >
                <BellIcon className="text-gray-600" size={24} />
                {notifications &&
                  notifications?.filter((notification) => !notification?.viewed)
                    ?.length > 0 && (
                    <span className="absolute -right-2 -top-2 ml-2 rounded-full bg-orange-400 px-2 py-1 text-xs font-semibold text-white">
                      {
                        notifications?.filter(
                          (notification) => !notification?.viewed
                        )?.length
                      }
                    </span>
                  )}
                <NotificationDropdown
                  isOpen={notificationsOpen}
                  notifications={notifications}
                  isLoading={loadingNotifications}
                  refetchNotifications={refetchNotifications}
                />
              </button>
              <button
                className="rounded-full bg-gray-200 p-2"
                onClick={() => {
                  setCurrentTab('settings');
                  navigate('/dashboard/settings/account');
                }}
              >
                <GearIcon className="text-gray-600" size={24} />
              </button>
              <div
                onClick={(e: any) => {
                  e.stopPropagation();
                  togglePopup();
                }}
                className="user-box-toggle relative flex items-center space-x-2 rounded-full bg-gray-200 px-2 py-1 hover:cursor-pointer"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
                  {((userRecord?.userType === Entity.Customer
                    ? userRecord?.driver?.first_name
                    : null) ?? 'T')[0].toUpperCase()}
                </div>
                {userRecord?.userType === Entity.Customer && (
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-800">
                      {userRecord?.driver?.first_name +
                        ' ' +
                        userRecord?.driver?.last_name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {userRecord?.business_name}
                    </span>
                  </div>
                )}
                <CaretDownIcon
                  size={20}
                  className={`${popupVisible ? 'rotate-180' : ''}`}
                  weight="bold"
                />
                {popupVisible && (
                  <div
                    ref={popupRef}
                    className="absolute right-0 top-[2.85rem] z-50 w-48 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                  >
                    <div>
                      {customerStatus === 'active' && (
                        <button
                          onClick={(e: any) => {
                            e.stopPropagation();
                            navigate('/dashboard/resources');
                            setPopupVisible(false);
                          }}
                          className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-gray-700 hover:bg-gray-100"
                        >
                          <InfoIcon size={20} />
                          <p>My Resources</p>
                        </button>
                      )}
                      <button
                        onClick={(e: any) => {
                          e.stopPropagation();
                          logout();
                          setPopupVisible(false);
                        }}
                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                      >
                        <SignOutIcon size={20} />
                        <p>Log Out</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case Entity.Vendor:
        return (
          <div className="flex w-full flex-row items-center justify-between bg-transparent align-middle">
            <div className="flex w-[30%] items-center pb-[16px]">
              {renderTitleOrSearchVendor()}
            </div>
            <div className="flex items-center space-x-4">
              {/* <button className="rounded-full bg-gray-200 p-2">
                <BellIcon className="text-gray-600" size={24} />
              </button>
              <button
                className="rounded-full bg-gray-200 p-2"
                onClick={() => {
                  setCurrentTab('Settings');
                  navigate('/dashboard/settings/system-preferences');
                }}
              >
                <GearIcon className="text-gray-600" size={24} />
              </button> */}
              <div
                onClick={(e: any) => {
                  e.stopPropagation();
                  togglePopup();
                }}
                className="relative flex items-center space-x-2 rounded-full bg-gray-200 px-2 py-1 hover:cursor-pointer"
              >
                {userRecord?.userType === Entity.Vendor &&
                userRecord?.logoUrl &&
                userRecord?.logoUrl?.length > 0 ? (
                  <img
                    src={userRecord?.logoUrl}
                    className="h-8 w-8 rounded-full"
                    alt="logo"
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
                    {((userRecord?.userType === Entity.Vendor
                      ? userRecord?.business_name
                      : null) ?? 'T')[0].toUpperCase()}
                  </div>
                )}
                {userRecord?.userType === Entity.Vendor && (
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-800">
                      {userRecord?.business_name}
                    </span>
                    {/* <span className="text-xs text-gray-500">
                      {userRecord?.business_name}
                    </span> */}
                  </div>
                )}
                <CaretDownIcon
                  size={20}
                  className={`${popupVisible ? 'rotate-180' : ''}`}
                  weight="bold"
                />
                {popupVisible && (
                  <div
                    ref={popupRef}
                    className="absolute right-0 top-[2.85rem] z-50 w-48 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                  >
                    <div className="flex flex-col">
                      <button
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setCurrentTab('Profile');
                          navigate('/dashboard/profile');
                          setPopupVisible(false);
                        }}
                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                      >
                        <UserIcon size={20} />
                        <p>Profile</p>
                      </button>
                      <button
                        onClick={(e: any) => {
                          e.stopPropagation();
                          logout();
                          setPopupVisible(false);
                        }}
                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                      >
                        <SignOutIcon size={20} />
                        <p>Log Out</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case Entity.PartnerAgent:
        return (
          <div className="flex w-full flex-row items-center justify-between bg-transparent align-middle">
            <div className="flex w-[30%] items-center pb-[16px]">
              {renderTitleOrSearchPartner()}
            </div>
            <div className="flex items-center space-x-4">
              {/* <button className="rounded-full bg-gray-200 p-2">
                <BellIcon className="text-gray-600" size={24} />
              </button> */}
              {/* <button
                className="rounded-full bg-gray-200 p-2"
                onClick={() => {
                  setCurrentTab('Settings');
                  navigate('/dashboard/settings/system-preferences');
                }}
              >
                <GearIcon className="text-gray-600" size={24} />
              </button> */}
              <div
                onClick={(e: any) => {
                  e.stopPropagation();
                  togglePopup();
                }}
                className="relative flex items-center space-x-2 rounded-full bg-gray-200 px-2 py-1 hover:cursor-pointer"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
                  {((userRecord?.userType === Entity.PartnerAgent
                    ? userRecord?.first_name
                    : null) ?? 'T')[0].toUpperCase()}
                </div>
                {userRecord?.userType === Entity.PartnerAgent && (
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-800">
                      {userRecord?.first_name + ' ' + userRecord?.last_name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {userRecord?.partner?.business_name}
                    </span>
                  </div>
                )}
                <CaretDownIcon
                  size={20}
                  className={`${popupVisible ? 'rotate-180' : ''}`}
                  weight="bold"
                />
                {popupVisible && (
                  <div
                    ref={popupRef}
                    className="absolute right-0 top-[2.85rem] z-50 w-48 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                  >
                    <div className="flex flex-col">
                      {/* <button
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setCurrentTab('Profile');
                          navigate('/dashboard/profile');
                          setPopupVisible(false);
                        }}
                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                      >
                        <User size={20} />
                        <p>Profile</p>
                      </button> */}
                      <button
                        onClick={(e: any) => {
                          e.stopPropagation();
                          logout();
                          setPopupVisible(false);
                        }}
                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                      >
                        <SignOutIcon size={20} />
                        <p>Log Out</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case Entity.Partner:
        return (
          <div className="flex w-full flex-row items-center justify-between bg-transparent align-middle">
            <div className="flex w-[30%] items-center pb-[16px]">
              {renderTitleOrSearchPartner()}
            </div>
            <div className="flex items-center space-x-4">
              {/* <button className="rounded-full bg-gray-200 p-2">
                <BellIcon className="text-gray-600" size={24} />
              </button> */}
              {/* <button
                className="rounded-full bg-gray-200 p-2"
                onClick={() => {
                  setCurrentTab('Settings');
                  navigate('/dashboard/settings/system-preferences');
                }}
              >
                <GearIcon className="text-gray-600" size={24} />
              </button> */}
              <div
                onClick={(e: any) => {
                  e.stopPropagation();
                  togglePopup();
                }}
                className="relative flex items-center space-x-2 rounded-full bg-gray-200 px-2 py-1 hover:cursor-pointer"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
                  {((userRecord?.userType === Entity.Partner
                    ? userRecord?.business_name
                    : null) ?? 'T')[0].toUpperCase()}
                </div>
                {userRecord?.userType === Entity.Partner && (
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-800">
                      {userRecord?.business_name}
                    </span>
                    {/* <span className="text-xs text-gray-500">
                      {userRecord?.business_name}
                    </span> */}
                  </div>
                )}
                <CaretDownIcon
                  size={20}
                  className={`${popupVisible ? 'rotate-180' : ''}`}
                  weight="bold"
                />
                {popupVisible && (
                  <div
                    ref={popupRef}
                    className="absolute right-0 top-[2.85rem] z-50 w-48 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                  >
                    <div className="flex flex-col">
                      {/* <button
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setCurrentTab('Profile');
                          navigate('/dashboard/profile');
                          setPopupVisible(false);
                        }}
                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                      >
                        <User size={20} />
                        <p>Profile</p>
                      </button> */}
                      <button
                        onClick={(e: any) => {
                          e.stopPropagation();
                          logout();
                          setPopupVisible(false);
                        }}
                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                      >
                        <SignOutIcon size={20} />
                        <p>Log Out</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case Entity.Driver:
        return (
          <div className="flex w-full flex-row items-center justify-between bg-transparent align-middle">
            <div className="flex w-[35%] items-center pb-[16px]">
              {renderTitleOrSearchDriver()}
            </div>
            <div className="flex items-center space-x-4">
              <div
                onClick={(e: any) => {
                  e.stopPropagation();
                  togglePopup();
                }}
                className="user-box-toggle relative flex items-center space-x-2 rounded-full bg-gray-200 px-2 py-1 hover:cursor-pointer"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
                  {((userRecord?.userType === Entity.Driver
                    ? userRecord?.first_name
                    : null) ?? 'T')[0].toUpperCase()}
                </div>
                {userRecord?.userType === Entity.Driver && (
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-800">
                      {userRecord?.first_name + ' ' + userRecord?.last_name}
                    </span>
                    <span className="text-sm text-gray-800">
                      {userRecord?.customer?.business_name}
                    </span>
                  </div>
                )}
                <CaretDownIcon
                  size={20}
                  className={`${popupVisible ? 'rotate-180' : ''}`}
                  weight="bold"
                />
                {popupVisible && (
                  <div
                    ref={popupRef}
                    className="absolute right-0 top-[2.85rem] z-50 w-48 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                  >
                    <div>
                      <button
                        onClick={(e: any) => {
                          e.stopPropagation();
                          logout();
                          setPopupVisible(false);
                        }}
                        className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                      >
                        <SignOutIcon size={20} />
                        <p>Log Out</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      {screenView === 'desktop' ? (
        <div className="w-full max-w-[1148px] items-center justify-center border-b border-gray-200 align-middle">
          {renderUserBar()}
        </div>
      ) : (
        <div className="relative max-h-[103px] w-full items-center justify-center align-middle">
          <img
            src={logoUrl ?? '/TachLogo.svg'}
            alt={logoUrl ? 'Company logo' : 'Tach Logo'}
            className={
              logoUrl
                ? 'absolute left-3 top-1/2 max-h-9 w-auto max-w-[120px] -translate-y-1/2 object-contain'
                : 'absolute left-3 top-4 w-20'
            }
            onError={(e) => {
              e.currentTarget.src = '/TachLogo.svg';
            }}
          />
          <ListIcon
            size={32}
            className="absolute right-3 top-3 text-base hover:cursor-pointer"
            onClick={() => setShowSideNav(!showSideNav)}
          />
          <div className="flex w-full flex-row items-center justify-center bg-white px-4 py-3 align-middle">
            <div className="flex items-center">
              {renderTitleOrSearchTablet()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TopBar;
